"use client";

import { Container } from "components/organisms/Container/Container";
import { Form } from "components/organisms/Form/Form";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { FC, useCallback, useMemo } from "react";
import { getFormFields } from "utils/form";
import { ContactFormSectionRecord, OfficeRecord } from "@graphql/types/graphql";
import { FormType } from "consts";
import {
  getOfficeOptionsMeta,
  matchOfficeOptionByQueryParams,
  transformGeneralFormValuesForAirtable,
} from "consts/general-contact-form";

import { Locale } from "consts/locale";
import { useUtmParams } from "utils/hooks/useUtmParams";
import { pathToHref } from "utils";
import s from "./DatoForm.module.scss";

interface Props {
  data: ContactFormSectionRecord;
  offices?: OfficeRecord[];
}

export const DatoForm: FC<Props> = ({ data: { formType, title, intro, anchor }, offices }) => {
  const locale = useLocale() as Locale;
  const searchParams = useSearchParams();
  const t = useTranslations();

  const officePrefillSlug = searchParams.get("office");
  const locationPrefillSlug = searchParams.get("location");

  const matchedOption = useMemo(
    () =>
      matchOfficeOptionByQueryParams(
        getOfficeOptionsMeta(offices),
        officePrefillSlug,
        locationPrefillSlug,
        offices,
      ),
    [offices, officePrefillSlug, locationPrefillSlug],
  );

  const formFields = useMemo(
    () =>
      getFormFields({
        formType: formType as FormType,
        locale,
        offices: formType === FormType.general ? offices : undefined,
        t,
      }),
    [locale, formType, t, offices],
  );
  const utm = useUtmParams();

  const defaultValues = useMemo(
    () =>
      formType === FormType.general
        ? {
            ...utm,
            web_referral: pathToHref(locale, searchParams.get("referral") ?? "/"),
            selected_office: matchedOption?.value ?? null,
            office_location: matchedOption?.locationSlug ?? locationPrefillSlug,
            locale,
          }
        : {},
    [formType, locale, locationPrefillSlug, matchedOption, searchParams, utm],
  );

  const transformValuesBeforeSubmit = useCallback(
    (values: Record<string, unknown>) => transformGeneralFormValuesForAirtable(values, offices),
    [offices],
  );

  if (!formFields) {
    return null;
  }

  return (
    <Container size="s" className={s.Container} {...(anchor && { id: anchor })}>
      {title && <h2 className={s.Title}>{title}</h2>}
      {intro && <p className={s.Intro}>{intro}</p>}
      <Form
        key={`${matchedOption?.value ?? ""}-${
          matchedOption?.locationSlug ?? locationPrefillSlug ?? ""
        }`}
        className={s.Form}
        fields={formFields}
        formType={formType as FormType}
        defaultValues={defaultValues}
        transformValuesBeforeSubmit={
          formType === FormType.general ? transformValuesBeforeSubmit : undefined
        }
      />
    </Container>
  );
};
